import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";
import { queryKeys } from "../../utilities/queryKeys";
import { getProviderDashboard } from "../../services/serviceProvider";
import { endOfDay, format } from "date-fns";
import { daysAgo, isDeadlineOverdue } from "../../utilities/helperFunctions";
import { DataTable } from "../../Components/ui/data-table";
import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "../../Components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../Components/ui/card";
import { DatePickerInput } from "../../Components/ui/date-picker-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../Components/ui/select";
import { type BadgeVariant } from "../../utilities/stageRegistry";
import {
  AlertTriangle,
  FileEdit,
  Clock,
  Receipt,
  CheckCircle,
  ClipboardList,
  Users,
  Building2,
  Search,
  X,
  type LucideIcon,
} from "lucide-react";

// ─── Stat card configuration ────────────────────────────────────────────────

type SeverityTier = "urgent" | "attention" | "info" | "success" | "neutral";

interface StatCardConfig {
  key: string;
  label: string;
  value: number;
  tier: SeverityTier;
  icon: LucideIcon;
}

const tierStyles: Record<SeverityTier, { bg: string; iconColor: string }> = {
  urgent: { bg: "bg-cDenied", iconColor: "text-cError" },
  attention: { bg: "bg-cLightYellow", iconColor: "text-amber-600" },
  info: { bg: "bg-cLightGreen", iconColor: "text-cSuccess" },
  success: { bg: "bg-[#4F9D7A]/15", iconColor: "text-cSuccess" },
  neutral: { bg: "bg-cLightGrey", iconColor: "text-muted-foreground" },
};

// Single source of truth for the stat cards: each card maps to a set of
// actionItem values. The same map drives the card counts AND the table filter
// when a card is clicked, so the two can never drift apart.
interface StatGroup {
  key: string;
  label: string;
  tier: SeverityTier;
  icon: LucideIcon;
  // A row belongs to the group if its (invoice-aware) actionItem is listed in
  // `actionItems`, OR — for lifecycle-stage cards — its raw sowStatus is listed
  // in `sowStatuses`. Stage cards match on sowStatus because the actionItem gets
  // overridden by invoice state and no longer reflects the SoW stage.
  actionItems?: string[];
  sowStatuses?: string[];
}

// Raw SoW statuses at "SoW signed" or later in the lifecycle
// (Ready to Begin Work → Project Closed). Drives the "Signed SoWs and
// Invoicing" card, which counts every project from signing onward.
const SIGNED_OR_LATER_STATUSES = [
  "Ready to Begin Work",
  "Work In Progress",
  "Ready for Invoice",
  "Invoice in Progress",
  "Project Closed",
];

const matchesStatGroup = (
  group: StatGroup,
  row: { actionItem: string; sowStatus: string }
) =>
  (group.actionItems?.includes(row.actionItem) ?? false) ||
  (group.sowStatuses?.includes(row.sowStatus) ?? false);

const STAT_GROUPS: StatGroup[] = [
  { key: "newRequest", label: "New SoW request", tier: "attention", icon: AlertTriangle,
    actionItems: ["New SoW Request"] },
  { key: "drafting", label: "SoWs being drafted", tier: "attention", icon: FileEdit,
    actionItems: ["SoW In Progress"] },
  { key: "underReview", label: "SoWs under review", tier: "info", icon: Clock,
    actionItems: ["SoW Under Review", "Awaiting Expert Approval"] },
  { key: "sowRevision", label: "SoW revision requested", tier: "attention", icon: FileEdit,
    actionItems: ["SoW Edits Requested"] },
  { key: "invoiceRevision", label: "Invoice revision requested", tier: "attention", icon: Receipt,
    actionItems: ["Invoice revision requested"] },
  { key: "signedAndInvoicing", label: "Signed SoWs and Invoicing", tier: "success", icon: CheckCircle,
    sowStatuses: SIGNED_OR_LATER_STATUSES },
];

function StatCard({ config, selected, onClick }: { config: StatCardConfig; selected: boolean; onClick: () => void }) {
  const isActive = config.value > 0;
  const style = tierStyles[config.tier];
  const Icon = config.icon;

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className={`cursor-pointer transition-all duration-150 hover:shadow-md hover:border-cPrimaryGreen/40 ${isActive ? style.bg : ""} ${selected ? "ring-2 ring-cPrimaryGreen" : ""}`}
    >
      <CardContent className="p-4 flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${isActive ? style.iconColor : "text-muted-foreground"}`} />
          <span className="text-2xl font-bold">{config.value}</span>
        </div>
        <p className="text-xs text-muted-foreground text-center leading-tight">{config.label}</p>
      </CardContent>
    </Card>
  );
}

// ─── Table columns ──────────────────────────────────────────────────────────

interface ProviderDashboardRow {
  sowId: string;
  companyName: string;
  practitionerName: string;
  actionItem: string;
  sowStatus: string;
  lastUpdated: string;
  tier: string;
  finalInvoiceDeadline: string | null;
  contactPerson: string;
  contactEmail: string;
  stageChangedAt?: string;
  daysInStage: string | number;
  // Populated by the backend when the SoW has a final invoice in
  // `revisionsRequested` state. Used to route the SP straight to
  // SpInvoiceDetails (where the InvoiceResubmitModal lives) instead of
  // EditSOW (whose "Edit Final Invoice" path is broken — calls newFinalInvoice
  // and dies on the duplicate-final guard).
  revisionInvoiceId?: string;
}

// Badge variants keyed by the actionItem string the backend returns.
// Using a local map instead of getBadgeVariant(sowStatus) because the backend
// overrides actionItem for invoice stages (e.g. sowStatus "Work In Progress"
// can display as "Retainer submitted") and the registry lookup would mismatch.
const spBadgeMap: Record<string, BadgeVariant> = {
  "New SoW Request": "warning",
  "SoW In Progress": "warning",
  "SoW Edits Requested": "warning",
  "SoW Under Review": "default",
  "Awaiting Your Signature": "warning",
  "SoW Signed": "success",
  "Work In Progress": "default",
  "Ready for Invoice": "success",
  "Retainer submitted": "secondary",
  "Retainer approved": "success",
  "Retainer sent to Finance": "success",
  "Final invoice submitted": "secondary",
  "Final invoice approved": "success",
  "Invoice sent to Finance": "success",
  "Invoice revision requested": "warning",
  "Project Closed": "outline",
  "Rejected": "destructive",
  "SoW Cancelled": "destructive",
};

const columns: ColumnDef<ProviderDashboardRow, unknown>[] = [
  {
    accessorKey: "companyName",
    header: "Startup",
    cell: ({ getValue }) => <span className="font-medium">{String(getValue())}</span>,
  },
  {
    accessorKey: "practitionerName",
    header: "Practitioner",
  },
  {
    accessorKey: "actionItem",
    header: "Status",
    cell: ({ getValue }) => {
      const status = String(getValue());
      return <Badge variant={spBadgeMap[status] ?? "outline"}>{status}</Badge>;
    },
  },
  {
    accessorKey: "tier",
    header: "Tier",
  },
  {
    accessorKey: "finalInvoiceDeadline",
    header: "Final Invoice Deadline",
    cell: ({ getValue }) => {
      const date = getValue();
      if (!date) return "-";
      const formatted = format(new Date(String(date)), "MMM do yyyy");
      return isDeadlineOverdue(String(date)) ? (
        <span className="text-cError font-medium">{formatted}</span>
      ) : (
        formatted
      );
    },
  },
  {
    accessorKey: "contactPerson",
    header: "Contact",
  },
  {
    accessorKey: "contactEmail",
    header: "Contact Email",
  },
  {
    accessorKey: "daysInStage",
    header: "Days in Stage",
  },
];

// ─── New provider onboarding empty state ────────────────────────────────────

function NewProviderOnboarding() {
  const navigate = useNavigate();

  const steps = [
    {
      icon: Building2,
      title: "Complete your firm profile",
      description: "Add your firm details, address, and areas of expertise.",
      action: () => navigate("/dashboard/firm-profile"),
      actionLabel: "Go to Firm Profile",
    },
    {
      icon: Users,
      title: "Add practitioners to your team",
      description: "Register the practitioners who will work on engagements.",
      action: () => navigate("/dashboard/practitioners"),
      actionLabel: "Manage Practitioners",
    },
    {
      icon: ClipboardList,
      title: "SoW requests will appear here",
      description: "When your firm is assigned to a startup, you'll see the request on this dashboard.",
      action: undefined,
      actionLabel: undefined,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Getting Started</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {steps.map((step, i) => {
          const Icon = step.icon;
          return (
            <div key={i} className="flex gap-4 items-start">
              <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-50 text-blue-600 shrink-0">
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium">{step.title}</p>
                <p className="text-sm text-muted-foreground mt-0.5">{step.description}</p>
                {step.action && (
                  <button
                    onClick={step.action}
                    className="text-sm text-blue-600 hover:text-blue-800 hover:underline mt-1"
                  >
                    {step.actionLabel} →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ─── SoW list filters ────────────────────────────────────────────────────────
// Mirrors the admin MembersList filter UX (collapsible panel, active-filter
// count badge, removable chips, "Clear all"), trimmed to the four parameters
// that matter to a service provider: Status, min Days in Stage, Practitioner,
// and Final Invoice Deadline before a chosen date. Pure local UI state — the
// filtered rows are derived from query data, never copied into state.

interface SowFilters {
  status: string;
  practitioner: string;
  minDaysInStage: number;
  deadlineBefore: Date | null;
  // Set by clicking a stat card; key into STAT_GROUPS, or "All".
  statGroup: string;
}

const DEFAULT_SOW_FILTERS: SowFilters = {
  status: "All",
  practitioner: "All",
  minDaysInStage: 0,
  deadlineBefore: null,
  statGroup: "All",
};

interface SowDashboardFiltersProps {
  filters: SowFilters;
  searchTerm: string;
  expanded: boolean;
  activeFilterCount: number;
  statusOptions: string[];
  practitionerOptions: string[];
  statGroupLabel: string | null;
  onFilterChange: <K extends keyof SowFilters>(key: K, value: SowFilters[K]) => void;
  onSearchChange: (term: string) => void;
  onReset: () => void;
  onToggleExpanded: () => void;
}

function SowDashboardFilters({
  filters,
  searchTerm,
  expanded,
  activeFilterCount,
  statusOptions,
  practitionerOptions,
  statGroupLabel,
  onFilterChange,
  onSearchChange,
  onReset,
  onToggleExpanded,
}: SowDashboardFiltersProps) {
  return (
    <div className="w-full bg-white border border-borders rounded-lg p-4 mb-4 relative z-40">
      {/* Header: toggle + active count + clear all */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={onToggleExpanded}
          className="text-sm font-medium text-text flex items-center gap-2 cursor-pointer hover:text-cSecondaryGreen transition-colors"
        >
          <svg
            className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`}
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
          >
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-cPrimaryGreen rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={onReset}
            className="text-sm text-cPrimaryGreen hover:text-cSecondaryGreen transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Search — always visible */}
      <div className={`relative${expanded ? " mb-3" : ""}`}>
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-text/40" />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by startup, practitioner, status, contact..."
          className="w-full pl-9 pr-9 py-2 text-sm border border-borders rounded focus:outline-none focus:ring-2 focus:ring-cPrimaryGreen"
        />
        {searchTerm && (
          <button
            onClick={() => onSearchChange("")}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-text/40 hover:text-text/70"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Collapsible filter options */}
      <div
        className="overflow-hidden transition-[max-height] duration-300 ease-in-out"
        style={{ maxHeight: expanded ? "400px" : "0px" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pb-1">
          {/* Status */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text/70">Status</label>
            <Select value={filters.status} onValueChange={(v) => onFilterChange("status", v)}>
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                {statusOptions.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Min Days in Stage */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text/70">Days in Stage more than</label>
            <input
              type="number"
              onWheel={(e) => e.currentTarget.blur()}
              min={0}
              value={filters.minDaysInStage || ""}
              onChange={(e) => onFilterChange("minDaysInStage", parseInt(e.target.value, 10) || 0)}
              placeholder="0 = All"
              className="text-sm border border-borders rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cPrimaryGreen"
            />
          </div>

          {/* Practitioner */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text/70">Practitioner</label>
            <Select value={filters.practitioner} onValueChange={(v) => onFilterChange("practitioner", v)}>
              <SelectTrigger className="text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                {practitionerOptions.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Final Invoice Deadline before */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-text/70">Final Invoice Deadline before</label>
            <DatePickerInput
              id="filter-deadline-before"
              selected={filters.deadlineBefore}
              onChange={(date) => onFilterChange("deadlineBefore", date)}
              dateFormat="MMM d, yyyy"
              placeholderText="Any date"
              isClearable
              className="text-sm border border-borders rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-cPrimaryGreen"
              showYearDropdown
              yearDropdownItemNumber={10}
            />
          </div>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-borders">
          {filters.statGroup !== "All" && statGroupLabel && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              {statGroupLabel}
              <button onClick={() => onFilterChange("statGroup", "All")} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
          {filters.status !== "All" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              Status: {filters.status}
              <button onClick={() => onFilterChange("status", "All")} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
          {filters.minDaysInStage > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              Days in Stage: {filters.minDaysInStage}+
              <button onClick={() => onFilterChange("minDaysInStage", 0)} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
          {filters.practitioner !== "All" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              Practitioner: {filters.practitioner}
              <button onClick={() => onFilterChange("practitioner", "All")} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
          {filters.deadlineBefore && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              Deadline before: {filters.deadlineBefore.toLocaleDateString()}
              <button onClick={() => onFilterChange("deadlineBefore", null)} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
          {searchTerm && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-cPrimaryGreen/10 text-cPrimaryGreen text-xs rounded">
              Search: &quot;{searchTerm}&quot;
              <button onClick={() => onSearchChange("")} className="hover:text-cSecondaryGreen">&times;</button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Dashboard ─────────────────────────────────────────────────────────

function ProviderDashboard() {
  const navigate = useNavigate();

  const { data: providerDashboardData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.provider(),
    queryFn: () => getProviderDashboard(),
  });

  const handleViewSowDetails = (row: ProviderDashboardRow) => {
    // For invoices in `revisionsRequested` state, route the SP directly to
    // SpInvoiceDetails so they can use the working "Edit & Resubmit" modal.
    // The SoW details page (EditSOW) has a button labeled "Edit Final Invoice"
    // that posts to /new-final-invoice and is rejected by the duplicate guard.
    if (row.revisionInvoiceId) {
      navigate(`/dashboard/invoice/${row.revisionInvoiceId}`);
      return;
    }
    navigate(`/dashboard/statement-of-work/${row.sowId}`);
  };

  const contentArray: ProviderDashboardRow[] = useMemo(
    () =>
      providerDashboardData.sowArray.map((h: any) => ({
        ...h,
        daysInStage: h.stageChangedAt ? daysAgo(new Date(h.stageChangedAt)) : "-",
      })),
    [providerDashboardData.sowArray]
  );

  // ─── Filtering ─────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState<SowFilters>(DEFAULT_SOW_FILTERS);

  const distinctSorted = (values: (string | undefined | null)[]) =>
    Array.from(new Set(values.filter((v): v is string => !!v && v !== "-"))).sort((a, b) =>
      a.localeCompare(b)
    );

  const statusOptions = useMemo(
    () => distinctSorted(contentArray.map((r) => r.actionItem)),
    [contentArray]
  );
  const practitionerOptions = useMemo(
    () => distinctSorted(contentArray.map((r) => r.practitionerName)),
    [contentArray]
  );

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const activeGroup = STAT_GROUPS.find((g) => g.key === filters.statGroup);
    return contentArray.filter((r) => {
      if (activeGroup && !matchesStatGroup(activeGroup, r)) return false;
      if (filters.status !== "All" && r.actionItem !== filters.status) return false;
      if (filters.practitioner !== "All" && (r.practitionerName || "") !== filters.practitioner) return false;
      // Rows with no stage date ("-") are excluded once a minimum is set.
      if (filters.minDaysInStage > 0) {
        if (typeof r.daysInStage !== "number" || r.daysInStage < filters.minDaysInStage) return false;
      }
      if (filters.deadlineBefore) {
        if (!r.finalInvoiceDeadline) return false;
        // Chosen day is inclusive — a deadline anywhere on that day still matches.
        if (new Date(r.finalInvoiceDeadline) > endOfDay(filters.deadlineBefore)) return false;
      }
      if (term) {
        const haystack = [r.companyName, r.practitionerName, r.actionItem, r.contactPerson, r.contactEmail, r.tier];
        if (!haystack.some((f) => String(f ?? "").toLowerCase().includes(term))) return false;
      }
      return true;
    });
  }, [contentArray, filters, searchTerm]);

  const activeFilterCount = [
    filters.statGroup !== "All",
    filters.status !== "All",
    filters.practitioner !== "All",
    filters.minDaysInStage > 0,
    filters.deadlineBefore !== null,
    searchTerm.length > 0,
  ].filter(Boolean).length;

  const statGroupLabel =
    STAT_GROUPS.find((g) => g.key === filters.statGroup)?.label ?? null;

  // Clicking a stat card toggles its group as the active table filter.
  const handleStatCardClick = (key: string) =>
    setFilters((prev) => ({ ...prev, statGroup: prev.statGroup === key ? "All" : key }));

  const handleFilterChange = <K extends keyof SowFilters>(key: K, value: SowFilters[K]) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleResetFilters = () => {
    setFilters(DEFAULT_SOW_FILTERS);
    setSearchTerm("");
  };

  // Stat card counts derived from the shared STAT_GROUPS map, so card counts
  // and the click-to-filter behavior always use the same actionItem sets.
  const statCards: StatCardConfig[] = STAT_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    tier: g.tier,
    icon: g.icon,
    value: contentArray.filter((r) => matchesStatGroup(g, r)).length,
  }));

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* No name in the greeting: one SP login is shared by multiple practitioners in the firm */}
      <h2>Welcome back!</h2>

      {contentArray.length === 0 ? (
        <NewProviderOnboarding />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {statCards.map(card => (
              <StatCard
                key={card.key}
                config={card}
                selected={filters.statGroup === card.key}
                onClick={() => handleStatCardClick(card.key)}
              />
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Your Statements of Work</CardTitle>
            </CardHeader>
            <CardContent className="px-2 sm:px-6">
              <SowDashboardFilters
                filters={filters}
                searchTerm={searchTerm}
                expanded={filtersExpanded}
                activeFilterCount={activeFilterCount}
                statusOptions={statusOptions}
                practitionerOptions={practitionerOptions}
                statGroupLabel={statGroupLabel}
                onFilterChange={handleFilterChange}
                onSearchChange={setSearchTerm}
                onReset={handleResetFilters}
                onToggleExpanded={() => setFiltersExpanded((p) => !p)}
              />
              {filteredRows.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">
                  No statements of work match your filters.
                </p>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredRows}
                  onRowClick={handleViewSowDetails}
                  enableSorting
                  enablePagination
                />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

export default ProviderDashboard;
